import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  LoadingOutlined,
  RedoOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useIntl } from "@umijs/max";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Empty,
  notification,
  Space,
  Typography,
  theme,
} from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type DownloadTask,
  FileDownloadStatus,
  DownloadTaskStatus,
  useGlobalDownloadStore,
} from "@/stores/useGlobalDownloadStore";
import {
  formatCompletedTime,
  formatDuration,
  formatSpeed,
} from "@/utils/common";

type DownloadCenterProps = {
  actionClassName: string;
};

type DesignToken = ReturnType<typeof theme.useToken>["token"];

type DownloadTaskItemProps = {
  task: DownloadTask;
  nowTs: number;
  token: DesignToken;
  statusIconMap: Record<DownloadTask["status"], React.ReactNode>;
  statusTextMap: Record<DownloadTask["status"], string>;
  retryTitle: string;
  onRetry: (taskId: string) => void;
  onRemove: (taskId: string) => void;
  onToggleExpand: (taskId: string) => void;
};

const DownloadTaskItem: React.FC<DownloadTaskItemProps> = React.memo(({
  task,
  nowTs,
  token,
  statusIconMap,
  statusTextMap,
  retryTitle,
  onRetry,
  onRemove,
  onToggleExpand,
}) => {
  const elapsedBaseTs =
    task.status === DownloadTaskStatus.DONE ||
    task.status === DownloadTaskStatus.FAILED
      ? task.updatedAt
      : nowTs;
  const itemProgressPercent =
    task.status === DownloadTaskStatus.ZIPPING
      ? task.zipProgress
      : task.progress;
  const safeItemProgressPercent = Math.max(
    0,
    Math.min(100, Math.round(itemProgressPercent)),
  );
  const downloadingBackground = `linear-gradient(90deg, ${token.colorPrimaryBg} 0%, ${token.colorPrimaryBg} ${safeItemProgressPercent}%, ${token.colorBgContainer} ${safeItemProgressPercent}%, ${token.colorBgContainer} 100%)`;
  const itemBackgroundMap: Record<DownloadTaskStatus, string> = {
    [DownloadTaskStatus.DOWNLOADING]: downloadingBackground,
    [DownloadTaskStatus.ZIPPING]: downloadingBackground,
    [DownloadTaskStatus.DONE]: token.colorSuccessBg,
    [DownloadTaskStatus.FAILED]: token.colorErrorBg,
  };
  const itemBackground =
    itemBackgroundMap[task.status] ?? token.colorBgContainer;
  const downloadElapsedSec = task.downloadStartedAt
    ? (elapsedBaseTs - task.downloadStartedAt) / 1000
    : 0;
  const zipElapsedSec =
    task.status === DownloadTaskStatus.ZIPPING && task.zipStartedAt
      ? (nowTs - task.zipStartedAt) / 1000
      : 0;

  return (
    <div
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: 12,
        marginBottom: 8,
        background: itemBackground,
        transition: "background 0.3s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Space size={8} style={{ minWidth: 0 }}>
          {task.type === "folder" ? (
            <FolderOpenOutlined style={{ color: token.colorPrimary }} />
          ) : (
            <DownloadOutlined style={{ color: token.colorPrimary }} />
          )}
          <Typography.Text ellipsis style={{ maxWidth: 280 }}>
            {task.name}
          </Typography.Text>
        </Space>
        <Space size={8}>
          {task.status === DownloadTaskStatus.FAILED && (
            <Button
              type="text"
              size="small"
              icon={<RedoOutlined />}
              title={retryTitle}
              onClick={() => onRetry(task.id)}
            />
          )}
          {(task.status === DownloadTaskStatus.DONE ||
            task.status === DownloadTaskStatus.FAILED) && (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onRemove(task.id)}
            />
          )}
        </Space>
      </div>

      {task.type === "folder" ? (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 4,
              fontSize: 10,
              color: token.colorTextSecondary,
            }}
          >
            <Space size={6}>
              {statusIconMap[task.status]}
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {statusTextMap[task.status]}
              </Typography.Text>
              {task.status !== DownloadTaskStatus.DONE &&
                safeItemProgressPercent > 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                    {`${task.downloadedFiles}/${task.totalFiles} · ${safeItemProgressPercent}%`}
                  </Typography.Text>
                )}
            </Space>
            <Space size={4}>
              {task.status === DownloadTaskStatus.ZIPPING ? (
                <span style={{ fontSize: 10 }}>
                  {formatDuration(zipElapsedSec)}
                </span>
              ) : task.status === DownloadTaskStatus.DONE ? (
                <span style={{ fontSize: 10 }}>
                  {formatCompletedTime(task.updatedAt)}
                </span>
              ) : (
                <span style={{ fontSize: 10 }}>
                  {formatSpeed(task.currentDownloadSpeedBps)} ·{" "}
                  {formatDuration(downloadElapsedSec)}
                </span>
              )}
              <Button
                type="text"
                size="small"
                style={{
                  padding: 0,
                  width: 14,
                  height: 14,
                  minWidth: 14,
                }}
                onClick={() => onToggleExpand(task.id)}
                icon={
                  <RightOutlined
                    style={{
                      fontSize: 10,
                      transform: task.expanded ? "rotate(90deg)" : undefined,
                      transition: "transform 0.2s",
                    }}
                  />
                }
              />
            </Space>
          </div>
          {task.expanded && (
            <div
              style={{
                marginTop: 6,
                borderTop: `1px dashed ${token.colorBorderSecondary}`,
                paddingTop: 8,
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {task.fileProgressList.map((item) => (
                <div
                  key={item.key}
                  style={{
                    marginBottom: 6,
                    padding: "2px 6px",
                    borderRadius: 6,
                    background:
                      item.status === FileDownloadStatus.DONE
                        ? token.colorSuccessBg
                        : item.status === FileDownloadStatus.FAILED
                          ? token.colorErrorBg
                          : item.status === FileDownloadStatus.PENDING
                            ? token.colorFillTertiary
                            : `linear-gradient(90deg, ${token.colorPrimaryBg} 0%, ${token.colorPrimaryBg} ${Math.max(0, Math.min(100, Math.round(item.percent)))}%, ${token.colorBgContainer} ${Math.max(0, Math.min(100, Math.round(item.percent)))}%, ${token.colorBgContainer} 100%)`,
                    transition: "background 0.3s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Typography.Text
                      style={{ fontSize: 10, maxWidth: 300 }}
                      ellipsis={{ tooltip: item.name }}
                    >
                      {item.name}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      {Math.round(item.percent)}%
                    </Typography.Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 4,
            fontSize: 10,
            color: token.colorTextSecondary,
          }}
        >
          <Space size={6}>
            {statusIconMap[task.status]}
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              {statusTextMap[task.status]}
            </Typography.Text>
            {task.status !== DownloadTaskStatus.DONE &&
              safeItemProgressPercent > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                  {`${safeItemProgressPercent}%`}
                </Typography.Text>
              )}
          </Space>
          <span>
            {task.status === DownloadTaskStatus.DONE
              ? formatCompletedTime(task.updatedAt)
              : `${formatSpeed(task.currentDownloadSpeedBps)} · ${formatDuration(downloadElapsedSec)}`}
          </span>
        </div>
      )}
    </div>
  );
});

const DownloadCenter: React.FC<DownloadCenterProps> = ({ actionClassName }) => {
  const intl = useIntl();
  const { token } = theme.useToken();
  const [notificationApi, contextHolder] = notification.useNotification();
  const [open, setOpen] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const prevTaskIdsRef = useRef<string[]>([]);
  const prevTaskStatusMapRef = useRef<
    Record<string, DownloadTaskStatus | undefined>
  >({});
  const tasks = useGlobalDownloadStore((state) => state.tasks);
  const toggleTaskExpanded = useGlobalDownloadStore(
    (state) => state.toggleTaskExpanded,
  );
  const removeTask = useGlobalDownloadStore((state) => state.removeTask);
  const retryTask = useGlobalDownloadStore((state) => state.retryTask);
  const clearCompletedTasks = useGlobalDownloadStore(
    (state) => state.clearCompletedTasks,
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const timer = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [open]);

  useEffect(() => {
    const prevTaskIds = prevTaskIdsRef.current;
    const currentTaskIds = tasks.map((item) => item.id);
    const hasNewTask = currentTaskIds.some((id) => !prevTaskIds.includes(id));
    if (hasNewTask) {
      setOpen(true);
      setNowTs(Date.now());
    }
    prevTaskIdsRef.current = currentTaskIds;
  }, [tasks]);

  useEffect(() => {
    const currentStatusMap: Record<string, DownloadTaskStatus | undefined> = {};
    tasks.forEach((task) => {
      currentStatusMap[task.id] = task.status;
    });

    const prevMap = prevTaskStatusMapRef.current;
    const isFirstRun = Object.keys(prevMap).length === 0;
    prevTaskStatusMapRef.current = currentStatusMap;

    if (isFirstRun) {
      return;
    }

    tasks.forEach((task) => {
      const prevStatus = prevMap[task.id];
      if (
        !open &&
        prevStatus &&
        prevStatus !== DownloadTaskStatus.DONE &&
        task.status === DownloadTaskStatus.DONE
      ) {
        notificationApi.success({
          message: intl.formatMessage(
            {
              id: "pages.game_static.downloadCenter.notify.success",
              defaultMessage: "下载 {name} 成功",
            },
            { name: task.name },
          ),
        });
      }
    });
  }, [tasks, intl, notificationApi, open]);

  const runningCount = useMemo(
    () =>
      tasks.filter(
        (task) =>
          task.status === DownloadTaskStatus.DOWNLOADING ||
          task.status === DownloadTaskStatus.ZIPPING,
      ).length,
    [tasks],
  );
  const handleRetry = useCallback(
    (taskId: string) => {
      void retryTask(taskId);
    },
    [retryTask],
  );

  const sortedTasks = useMemo(() => {
    const statusRank: Record<DownloadTaskStatus, number> = {
      [DownloadTaskStatus.DOWNLOADING]: 0,
      [DownloadTaskStatus.ZIPPING]: 0,
      [DownloadTaskStatus.FAILED]: 1,
      [DownloadTaskStatus.DONE]: 2,
    };
    return [...tasks].sort((a, b) => {
      const rankDiff = statusRank[a.status] - statusRank[b.status];
      if (rankDiff !== 0) {
        return rankDiff;
      }
      if (a.status === DownloadTaskStatus.DONE) {
        return b.updatedAt - a.updatedAt;
      }
      return b.createdAt - a.createdAt;
    });
  }, [tasks]);

  const statusIconMap = useMemo<Record<DownloadTaskStatus, React.ReactNode>>(
    () => ({
      [DownloadTaskStatus.DOWNLOADING]: (
        <LoadingOutlined style={{ color: token.colorPrimary }} />
      ),
      [DownloadTaskStatus.ZIPPING]: (
        <LoadingOutlined style={{ color: token.colorPrimary }} />
      ),
      [DownloadTaskStatus.DONE]: (
        <CheckCircleOutlined style={{ color: token.colorSuccess }} />
      ),
      [DownloadTaskStatus.FAILED]: (
        <CloseCircleOutlined style={{ color: token.colorError }} />
      ),
    }),
    [token.colorPrimary, token.colorSuccess, token.colorError],
  );

  const statusTextMap = useMemo<Record<DownloadTaskStatus, string>>(
    () => ({
      [DownloadTaskStatus.DOWNLOADING]: intl.formatMessage({
        id: "pages.game_static.downloadCenter.status.downloading",
      }),
      [DownloadTaskStatus.ZIPPING]: intl.formatMessage({
        id: "pages.game_static.downloadCenter.status.zipping",
      }),
      [DownloadTaskStatus.DONE]: intl.formatMessage({
        id: "pages.game_static.downloadCenter.status.done",
      }),
      [DownloadTaskStatus.FAILED]: intl.formatMessage({
        id: "pages.game_static.downloadCenter.status.failed",
      }),
    }),
    [intl],
  );
  const retryTitle = intl.formatMessage({
    id: "pages.game_static.downloadCenter.action.retry",
    defaultMessage: "重新下载",
  });

  return (
    <>
      {contextHolder}
      <div className={actionClassName} onClick={() => setOpen(true)}>
        <Badge count={runningCount} size="small">
          <DownloadOutlined style={{ fontSize: "18px", cursor: "pointer" }} />
        </Badge>
      </div>
      <Drawer
        placement="right"
        width={520}
        open={open}
        styles={{
          wrapper: {
            zIndex: 2000,
          },
          body: {
            padding: 10,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          },
        }}
        onClose={() => setOpen(false)}
        title={intl.formatMessage({
          id: "pages.game_static.downloadCenter.title",
        })}
        extra={
          <Button
            size="small"
            type="link"
            disabled={tasks.length === 0}
            onClick={clearCompletedTasks}
          >
            {intl.formatMessage({
              id: "pages.game_static.downloadCenter.clear",
            })}
          </Button>
        }
      >
        <Alert
          showIcon
          type="warning"
          style={{ marginBottom: 10, fontSize: 10 }}
          message={intl.formatMessage({
            id: "pages.game_static.downloadCenter.refreshWarning",
            defaultMessage:
              "提示：刷新界面会导致下载失败，且不会保留下载记录。",
          })}
        />
        {tasks.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={intl.formatMessage({
              id: "pages.game_static.downloadCenter.empty",
            })}
            style={{ margin: "20px 0" }}
          />
        ) : (
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {sortedTasks.map((task) => (
              <DownloadTaskItem
                key={task.id}
                task={task}
                nowTs={nowTs}
                token={token}
                statusIconMap={statusIconMap}
                statusTextMap={statusTextMap}
                retryTitle={retryTitle}
                onRetry={handleRetry}
                onRemove={removeTask}
                onToggleExpand={toggleTaskExpanded}
              />
            ))}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default DownloadCenter;
